import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { copiarModelo } from '@/api/client';
import {
  fetchModelosPorFinalidade,
  type Configuracao,
  type ModeloResumo,
} from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useAuthOpcional } from '@/context/AuthProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { useWizardSave } from './WizardSaveContext';
import { useAdvogadoEmEdicao, useGravarPadroes } from './advogadoEmEdicao';

const SEM_MODELO = '__none__';

/** Volta do editor de modelos para este passo do Secretário. */
const VOLTAR_AO_PASSO = '/app/secretario-ia?tab=config&passo=areas&modo=personalizar';
// No escritório: o participante volta ao perfil dele; quem gere, à lista de advogados.
const VOLTAR_AO_PERFIL = '/app/secretario-ia?tab=perfil';
const VOLTAR_AOS_ADVOGADOS = '/app/secretario-ia?tab=config&passo=advogados';
/** O perfil (ou o padrão do escritório) aberto no passo Advogados — o ``?advogado=`` dele. */
export const PADRAO_DO_ESCRITORIO = 'escritorio';
const voltarAoPerfil = (id: string) => `${VOLTAR_AOS_ADVOGADOS}&advogado=${encodeURIComponent(id)}`;

/**
 * Modelos PADRÃO (gerais) de contrato e procuração da configuração. Sem eles o
 * Secretário ativa, mas faz só o pré-atendimento — é a partir deles que gera os
 * instrumentos. Cada área pode sobrescrever opcionalmente; sem override, usa estes.
 */
interface ModelosPadraoProps {
  config: Configuracao;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Chamado ao salvar com sucesso (o pai colapsa e abre a próxima seção). */
  onSaved?: () => void;
}

export function ModelosPadrao({ config, open, onOpenChange, onSaved }: ModelosPadraoProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  // No escritório, os modelos padrão do advogado em edição (§4.4).
  const advogado = useAdvogadoEmEdicao();
  const gravar = useGravarPadroes();
  // Editar faz uma cópia SUA do modelo: no perfil de um colega ela não valeria para
  // ele (o modelo do perfil tem de ser dele, do sistema ou do escritório).
  const meuId = useAuthOpcional()?.user?.id;
  // O padrão do escritório só vincula modelo do sistema ou do escritório: a cópia "sua"
  // seria pessoal e o backend a recusaria (ficaria órfã).
  const podeEditar = advogado ? advogado.id === meuId : config.escopo !== 'ESCRITORIO';
  // O padrão do escritório mora no passo Advogados (o wizard do escritório não tem "areas").
  const voltar = !advogado
    ? config.escopo === 'ESCRITORIO'
      ? voltarAoPerfil(PADRAO_DO_ESCRITORIO)
      : VOLTAR_AO_PASSO
    : config.papel === 'PARTICIPANTE'
      ? VOLTAR_AO_PERFIL
      : voltarAoPerfil(advogado.id);

  const [contratoId, setContratoId] = useState(config.template_contrato_id ?? SEM_MODELO);
  const [procuracaoId, setProcuracaoId] = useState(config.template_procuracao_id ?? SEM_MODELO);

  const { data: modelosContrato, isSuccess: contratoCarregou, error: erroContrato } = useQuery({
    queryKey: queryKeys.secretario.modelos('CONTRATO_HONORARIOS', advogado?.id),
    queryFn: () =>
      advogado
        ? fetchModelosPorFinalidade('CONTRATO_HONORARIOS', advogado.id)
        : fetchModelosPorFinalidade('CONTRATO_HONORARIOS'),
  });
  const { data: modelosProcuracao, isSuccess: procuracaoCarregou, error: erroProcuracao } = useQuery({
    queryKey: queryKeys.secretario.modelos('PROCURACAO', advogado?.id),
    queryFn: () =>
      advogado ? fetchModelosPorFinalidade('PROCURACAO', advogado.id) : fetchModelosPorFinalidade('PROCURACAO'),
  });
  // Falha na busca dos modelos NÃO pode ser silenciosa: sem isto o select de um
  // campo obrigatório apareceria vazio, sem explicação (convenção: Alert inline).
  const erroModelos = erroContrato ?? erroProcuracao;

  // Só sinaliza "nenhum modelo" depois que a query resolveu — durante o load (data
  // undefined) ou em erro, não afirmar ausência (induziria a criar duplicado).
  const semContrato = contratoCarregou && modelosContrato.length === 0;
  const semProcuracao = procuracaoCarregou && modelosProcuracao.length === 0;

  // Resumo/abertura derivam do estado SALVO (config), não da seleção em edição.
  const definidos = !!config.template_contrato_id && !!config.template_procuracao_id;
  // No escritório o padrão é opcional: falta dele não é alerta.
  const doEscritorio = !advogado && config.escopo === 'ESCRITORIO';
  // A herança é campo a campo (``modelo_efetivo``): o resumo diz qual é dele.
  const proprios = advogado
    ? advogado.contratoProprio && advogado.procuracaoPropria
      ? 'Próprios'
      : advogado.contratoProprio
        ? 'Contrato próprio; procuração do escritório'
        : advogado.procuracaoPropria
          ? 'Procuração própria; contrato do escritório'
          : null
    : 'Contrato e procuração';
  const summary = advogado?.herdaModelos && definidos ? (
    <span className="text-muted-foreground">Usa os do escritório</span>
  ) : definidos && proprios ? (
    <span className="flex items-center gap-1 text-emerald-600">
      <Check size={14} /> {proprios}
    </span>
  ) : doEscritorio ? (
    <span className="text-muted-foreground">Não definidos</span>
  ) : (
    <span className="text-amber-600">Defina contrato e procuração</span>
  );

  // Volta a herdar os do escritório: o perfil fica sem modelos próprios. Depois disso,
  // e depois de sair para o editor (que já gravou a cópia), o auto-save não grava por cima.
  const naoSalvarAoSair = useRef(false);
  const herdar = useMutation({
    mutationFn: () => {
      naoSalvarAoSair.current = true;
      return gravar({ template_contrato_id: null, template_procuracao_id: null });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('Voltaram a valer os modelos do escritório.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const contrato = contratoId === SEM_MODELO ? null : contratoId;
      const procuracao = procuracaoId === SEM_MODELO ? null : procuracaoId;
      if (!advogado) return gravar({ template_contrato_id: contrato, template_procuracao_id: procuracao });
      // No perfil, só o que ele mudou vira dele: o campo intocado segue herdado (senão o
      // modelo do escritório ficava congelado no perfil e a troca no escritório não
      // chegava a ele).
      const payload: { template_contrato_id?: string | null; template_procuracao_id?: string | null } = {};
      if (contrato !== (config.template_contrato_id ?? null)) payload.template_contrato_id = contrato;
      if (procuracao !== (config.template_procuracao_id ?? null)) payload.template_procuracao_id = procuracao;
      return Object.keys(payload).length > 0 ? gravar(payload) : null;
    },
    // onSaved (colapsa/avança a seção) fica no clique do botão — este save também
    // roda no auto-save da navegação, que não deve mexer nas seções sozinho.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      showSuccess('Modelos salvos.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // Editar o modelo escolhido. O do sistema ninguém altera: editar é fazer uma
  // cópia SUA, que passa a ser o modelo padrão do Secretário — senão o advogado
  // editaria a cópia e o Secretário continuaria gerando pelo original.
  const editar = useMutation({
    mutationFn: async ({ campo, modelo }: { campo: 'contrato' | 'procuracao'; modelo: ModeloResumo }) => {
      // Sair para o editor desmonta o wizard sem o auto-save da navegação entre
      // passos: a seleção ainda não salva é gravada aqui, senão o advogado
      // editaria o modelo escolhido e o Secretário seguiria gerando pelo antigo.
      const editado = modelo.proprio ? modelo.id : (await copiarModelo(modelo.id)).id;
      const contrato = campo === 'contrato' ? editado : contratoId === SEM_MODELO ? null : contratoId;
      const procuracao = campo === 'procuracao' ? editado : procuracaoId === SEM_MODELO ? null : procuracaoId;
      if (
        contrato !== (config.template_contrato_id ?? null) ||
        procuracao !== (config.template_procuracao_id ?? null)
      ) {
        await gravar({ template_contrato_id: contrato, template_procuracao_id: procuracao });
      }
      return editado;
    },
    onSuccess: (id, { modelo }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });
      queryClient.invalidateQueries({ queryKey: queryKeys.secretario.modelosAll });
      queryClient.invalidateQueries({ queryKey: queryKeys.modelos.all });
      if (!modelo.proprio) showSuccess('Criamos uma cópia sua do modelo, já em uso pelo Secretário.');
      naoSalvarAoSair.current = true;
      navigate(`/app/modelos/${id}/editar?voltar=${encodeURIComponent(voltar)}`);
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  const botaoEditar = (campo: 'contrato' | 'procuracao', selecionado: string, modelos: ModeloResumo[] | undefined) => {
    const modelo = modelos?.find((m) => m.id === selecionado);
    if (!podeEditar) return null;
    return (
      <Button
        type="button"
        variant="outline"
        className="shrink-0"
        disabled={!modelo || editar.isPending}
        onClick={() => modelo && editar.mutate({ campo, modelo })}
        aria-label={campo === 'contrato' ? 'Editar o modelo de contrato' : 'Editar o modelo de procuração'}
      >
        {editar.isPending && editar.variables?.campo === campo ? (
          <Spinner size={16} className="mr-2 text-inherit" />
        ) : (
          <Pencil className="mr-2 h-4 w-4" />
        )}
        Editar
      </Button>
    );
  };

  // Auto-save ao navegar (nunca perder a seleção não salva).
  const registerFlush = useWizardSave();
  const { mutate, isPending } = mutation;
  const dirty =
    (contratoId === SEM_MODELO ? null : contratoId) !== (config.template_contrato_id ?? null) ||
    (procuracaoId === SEM_MODELO ? null : procuracaoId) !== (config.template_procuracao_id ?? null);
  useEffect(() => {
    registerFlush('modelos-padrao', dirty && !isPending ? () => !naoSalvarAoSair.current && mutate() : null);
    return () => registerFlush('modelos-padrao', null);
  }, [dirty, isPending, mutate, registerFlush]);

  return (
    <CollapsibleCard
      title={advogado ? 'Modelos' : doEscritorio ? 'Modelos do escritório' : 'Modelos padrão'}
      summary={summary}
      open={open}
      onOpenChange={onOpenChange}
    >
      <p className="text-sm text-muted-foreground">
        {advogado?.herdaModelos
          ? 'Hoje valem os do escritório. Mudar aqui cria os modelos próprios deste advogado. '
          : doEscritorio
            ? 'Valem para o advogado sem modelos próprios. '
            : ''}
        O Secretário gera o contrato e a procuração com estes modelos; a área pode ter os seus.
      </p>

      {erroModelos != null && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Não foi possível carregar os modelos: {classifyError(erroModelos).message}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="padraoContrato">
          Contrato de honorários
        </Label>
        <div className="flex gap-2">
          <Select value={contratoId} onValueChange={setContratoId}>
            <SelectTrigger id="padraoContrato">
              <SelectValue placeholder="Selecione um modelo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SEM_MODELO}>Nenhum</SelectItem>
              {(modelosContrato ?? []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.titulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {botaoEditar('contrato', contratoId, modelosContrato)}
        </div>
        {semContrato && (
          <p className="text-xs text-amber-600">
            Nenhum modelo com a finalidade Contrato de honorários. Crie um em Modelos e selecione essa
            finalidade.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="padraoProcuracao">
          Procuração
        </Label>
        <div className="flex gap-2">
          <Select value={procuracaoId} onValueChange={setProcuracaoId}>
            <SelectTrigger id="padraoProcuracao">
              <SelectValue placeholder="Selecione um modelo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SEM_MODELO}>Nenhum</SelectItem>
              {(modelosProcuracao ?? []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.titulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {botaoEditar('procuracao', procuracaoId, modelosProcuracao)}
        </div>
        {semProcuracao && (
          <p className="text-xs text-amber-600">
            Nenhum modelo com a finalidade Procuração. Crie um em Modelos e selecione essa finalidade.
          </p>
        )}
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {advogado && !advogado.herdaModelos && advogado.escritorioTemModelos && (
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => herdar.mutate()}
            disabled={herdar.isPending}
          >
            {herdar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            Usar os do escritório
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => mutation.mutate(undefined, { onSuccess: () => onSaved?.() })}
          disabled={mutation.isPending || (!!advogado && !dirty)}
        >
          {mutation.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
          Salvar modelos
        </Button>
      </div>
    </CollapsibleCard>
  );
}
